import { useCallback, useEffect, useMemo, useState } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { ACTIVE_TIMELINE_DATE_STORAGE_KEY } from '../shared/DashboardViewContext';
import { auth } from '../../../services/firebase';
import { getE2EMockData, isE2EMockEnabled } from '../../../services/e2eMock';
import { getAllQuickTagOptions, getQuickTagDisplay, loadCustomQuickTags } from '../../../utils/quickTags';
import { getCalendarDateKey } from '../../../utils/calendarDateKey';

const useMobileTimelineFilters = ({ activeChildEntries }) => {
  const [searchText, setSearchText] = useState('');
  const [activeEntryTypes, setActiveEntryTypes] = useState([]);
  const [selectedDate, setSelectedDate] = useState(() => new Date());
  const [customQuickTags, setCustomQuickTags] = useState([]);
  const [timelineFilterSheetOpen, setTimelineFilterSheetOpen] = useState(false);
  const [timelineImportantOnly, setTimelineImportantOnly] = useState(false);
  const [timelineTagFilters, setTimelineTagFilters] = useState([]);
  const [timelineUserRoles, setTimelineUserRoles] = useState([]);
  const [timelineDatePickerAnchor, setTimelineDatePickerAnchor] = useState(null);

  useEffect(() => {
    const selectedDateKey = getCalendarDateKey(selectedDate);
    if (selectedDateKey && typeof window !== 'undefined') {
      window.localStorage.setItem(ACTIVE_TIMELINE_DATE_STORAGE_KEY, selectedDateKey);
      window.dispatchEvent(new CustomEvent('captureez:timeline-date-changed', {
        detail: { dateKey: selectedDateKey },
      }));
    }
  }, [selectedDate]);

  useEffect(() => {
    if (isE2EMockEnabled()) {
      setCustomQuickTags(loadCustomQuickTags(getE2EMockData().authUser?.uid));
      return undefined;
    }

    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCustomQuickTags(loadCustomQuickTags(user?.uid));
    });

    return () => unsubscribe();
  }, []);

  const clearTimelineFilters = useCallback(() => {
    setSearchText('');
    setActiveEntryTypes([]);
    setTimelineImportantOnly(false);
    setTimelineTagFilters([]);
    setTimelineUserRoles([]);
    setTimelineDatePickerAnchor(null);
  }, []);

  const resetTimelineFilters = useCallback(() => {
    clearTimelineFilters();
    setTimelineFilterSheetOpen(false);
    setSelectedDate(new Date());
  }, [clearTimelineFilters]);

  const availableTimelineTags = useMemo(() => {
    const tagMap = new Map();

    getAllQuickTagOptions(customQuickTags).forEach((tag) => {
      if (!tagMap.has(tag.key)) {
        tagMap.set(tag.key, tag);
      }
    });

    activeChildEntries.forEach((entry) => {
      (Array.isArray(entry.tags) ? entry.tags : []).forEach((tag) => {
        const key = String(tag || '').trim();
        if (!key || tagMap.has(key)) {
          return;
        }

        tagMap.set(key, getQuickTagDisplay(key, customQuickTags));
      });
    });

    return Array.from(tagMap.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [activeChildEntries, customQuickTags]);

  const timelineFiltersValue = useMemo(() => ({
    searchText: searchText || undefined,
    entryTypes: activeEntryTypes.length > 0 ? activeEntryTypes : undefined,
    userRoles: timelineUserRoles.length > 0 ? timelineUserRoles : undefined,
    importantOnly: timelineImportantOnly || undefined,
    tagFilters: timelineTagFilters.length > 0 ? timelineTagFilters : undefined,
  }), [activeEntryTypes, searchText, timelineImportantOnly, timelineTagFilters, timelineUserRoles]);

  const handleTimelineFiltersChange = useCallback((nextFilters) => {
    setSearchText(nextFilters?.searchText || '');
    setActiveEntryTypes(nextFilters?.entryTypes || []);
  }, []);

  const handleTimelineDatePreset = useCallback((dateValue) => {
    setSelectedDate(dateValue);
    setTimelineDatePickerAnchor(null);
  }, []);

  const handleTimelineDatePickerClose = useCallback(() => {
    setTimelineDatePickerAnchor(null);
  }, []);

  const openTimelineCalendar = useCallback((event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    setTimelineDatePickerAnchor(event.currentTarget);
  }, []);

  const openTimelineFilterDatePicker = useCallback(() => {
    setTimelineDatePickerAnchor((prev) => prev || document.body);
  }, []);

  const toggleTimelineTagFilter = useCallback((tagKey) => {
    setTimelineTagFilters((prev) => (
      prev.includes(tagKey)
        ? prev.filter((key) => key !== tagKey)
        : [...prev, tagKey]
    ));
  }, []);

  return {
    availableTimelineTags,
    clearTimelineFilters,
    handleTimelineDatePickerClose,
    handleTimelineDatePreset,
    handleTimelineFiltersChange,
    openTimelineCalendar,
    openTimelineFilterDatePicker,
    resetTimelineFilters,
    selectedDate,
    setSelectedDate,
    timelineDatePickerAnchor,
    timelineFiltersValue,
    timelineFilterSheetOpen,
    setTimelineFilterSheetOpen,
    timelineImportantOnly,
    setTimelineImportantOnly,
    timelineTagFilters,
    timelineUserRoles,
    setTimelineUserRoles,
    toggleTimelineTagFilter,
  };
};

export default useMobileTimelineFilters;
